/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Autocomplete, autocompleteClasses } from "@mui/material";
import { translationKeys, useTranslation } from "../../../Internationalisierung/i18nBasis";
import useErrors from "../../../Utils/userErrors";
import withTooltip from "../../HOC/withTooltip";
import AutocompleteRenderInput from "../Components/AutocompleteRenderInput";
import useOptionsQuery from "../useOptionsQuery";
import AutocompleteInputProps from "./AutocompleteInputProps";

export default withTooltip(function AutocompleteInput(props: AutocompleteInputProps) {
    const { error, errorTexts, clear } = useErrors(props);
    const { t } = useTranslation();
    const { options, execute, executeDebounced } = useOptionsQuery(props.executeQuery);

    return (
        <Autocomplete
            ListboxProps={{ className: "lan-autocompleteinput-clarity" }}
            freeSolo
            forcePopupIcon
            autoComplete
            autoHighlight
            value={props.value}
            onBlur={props.onBlur}
            onOpen={(e) => {
                // Initial die Optionen laden, wenn das Popup aufgeklickt wird
                if (!options.loading && (options.value.length < 1 || !!props.alwaysExecuteQueryOnOpen)) {
                    execute(props.value);
                }
            }}
            onInputChange={(e, value, reason) => {
                if (reason === "input" && !props.keepErrorsOnChange) {
                    clear();
                }

                if (reason === "reset" && value === props.value) {
                    return;
                }

                props.onChange(value);
                executeDebounced(value);
            }}
            filterOptions={(options, filterOptionsState) => {
                // Wir liefern hier einfach die Optionen. Diese werden ja bereits vom Server
                // gefiltert geliefert. Dies macht die Eingabe performanter und es kommt zu keinem
                // Flackern des Popups.
                return options;
            }}
            loading={options.loading}
            loadingText={props.loadingText ?? t(translationKeys.autoComplete.laden)}
            options={options.loading ? [] : options.value}
            disabled={props.readonly}
            disableClearable={props.disableClearable ?? true}
            clearText={t(translationKeys.autoComplete.leeren)}
            closeText={t(translationKeys.autoComplete.schliessen)}
            openText={t(translationKeys.autoComplete.oeffnen)}
            noOptionsText={t(translationKeys.autoComplete.keineOptionen)}
            sx={{
                [`& ${autocompleteClasses.popupIndicator}, & ${autocompleteClasses.clearIndicator}`]: {
                    // Korrigiert Wechselwirkung mit IconButton auf selber View
                    padding: "4px !important",
                },
            }}
            renderOption={props.renderOption}
            renderInput={params => (
                <AutocompleteRenderInput
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        ...props.InputProps,
                        endAdornment: (
                            <>
                                {params.InputProps.endAdornment}
                                {props.InputProps?.endAdornment}
                            </>
                        ),
                    }}
                    inputProps={{ ...params.inputProps, ...props.inputProps, }}
                    loading={options.loading}
                    caption={props.caption}
                    required={props.required}
                    autoFocus={props.autoFocus}
                    disabled={!!props.readonly}
                    placeholder={props.placeholder}
                    error={error}
                    errors={errorTexts}
                />
            )}
        />
    )
});
